// Shared types + validation used by both the API (Lambda) and the web SPA.
// Keep this dependency-free so it bundles cleanly into both targets.

/** A cap is either a fixed maximum or "unlimited" (no fixed limit). */
export type CapValue = number | "unlimited";

export interface RsvpCaps {
  /** Max adults a guest may bring, including themselves. */
  adults: CapValue;
  /** Max kids a guest may bring (roughly ages 5-18). */
  kids: CapValue;
}

/** A sane server-side ceiling so "unlimited" can't be abused with huge values. */
export const UNLIMITED_HARD_MAX = 30;

export function capLimit(cap: CapValue): number {
  return cap === "unlimited" ? UNLIMITED_HARD_MAX : cap;
}

/** Validate a single cap from untrusted input. */
export function parseCap(v: unknown): CapValue | null {
  if (v === "unlimited") return "unlimited";
  const n = Number(v);
  if (Number.isInteger(n) && n >= 0 && n <= UNLIMITED_HARD_MAX) return n;
  return null;
}

/** Validate a caps object from untrusted input; null if malformed. */
export function parseCaps(input: unknown): RsvpCaps | null {
  if (!input || typeof input !== "object") return null;
  const rec = input as Record<string, unknown>;
  const adults = parseCap(rec.adults);
  const kids = parseCap(rec.kids);
  if (adults === null || kids === null) return null;
  return { adults, kids };
}

export interface MediaImage {
  /** Path relative to the site root, e.g. "/media/<eventId>/01.jpg". */
  src: string;
  alt?: string;
}

export interface MediaAudio {
  /** Ordered <source> list; browser picks the first it can play. */
  sources: { src: string; type: string }[];
}

export interface MediaVideo {
  /** Ordered <source> list; browser picks the first it can play. */
  sources: { src: string; type: string }[];
  /** Poster frame shown before playback (e.g. the first photo). */
  poster?: string;
}

export interface MediaSection {
  title?: string;
  body?: string;
  /** Optional image path, relative to site root. */
  image?: string;
}

export interface MediaManifest {
  /** Intro photos. Used for the video poster and the static-scroll fallback. */
  images: MediaImage[];
  /**
   * Primary intro video (photos + music in one file). When present it is the
   * centerpiece; the images act as the fallback. Optional.
   */
  video?: MediaVideo;
  /** Standalone background audio, if there's no video. Optional. */
  audio?: MediaAudio;
  /** Sections used as captions for the static-scroll fallback. */
  sections: MediaSection[];
}

export interface EventConfig {
  eventId: string;
  title: string;
  /** ISO date string. */
  date: string;
  /** Venue name, e.g. "Mulholland Hills Country Club". */
  location?: string;
  /** Street address, shown beneath the venue name. */
  address?: string;
  /** Free-form theme tokens (colors, fonts) consumed by the SPA. */
  theme?: Record<string, string>;
  rsvpCaps: RsvpCaps;
  /** When true, guests can no longer submit or change their RSVP. */
  rsvpClosed?: boolean;
  media: MediaManifest;
  invitationSender?: {
    email: string;
    name?: string;
  };
}

export type RsvpStatus = "yes" | "no";

export interface Rsvp {
  status: RsvpStatus;
  adults: number;
  kids: number;
  /** ISO timestamp. */
  respondedAt: string;
}

export interface Guest {
  token: string;
  eventId: string;
  name: string;
  email?: string;
  phone?: string;
  isTest?: boolean;
  invitation?: {
    status: "sent" | "failed";
    sentAt?: string;
    emailSent?: boolean;
    smsSent?: boolean;
    error?: string;
  };
  /** ISO timestamp of first time the guest opened their invite link. */
  viewedAt?: string;
  /** Per-guest caps; falls back to the event defaults when absent. */
  caps?: RsvpCaps;
  revoked?: boolean;
  rsvp?: Rsvp;
  tableNumber?: string;
}

/** The caps that actually apply to a guest: their own, or the event default. */
export function effectiveCaps(
  guest: { caps?: RsvpCaps },
  event: { rsvpCaps: RsvpCaps }
): RsvpCaps {
  return guest.caps ?? event.rsvpCaps;
}

// ---- API payloads -------------------------------------------------------

export interface InviteValidResponse {
  valid: true;
  guestName: string;
  event: EventConfig;
  /** The caps that apply to this specific guest (their own or the default). */
  caps: RsvpCaps;
  /** Present if this guest already responded. */
  rsvp?: Rsvp;
  /** Assigned table number, if seating has been set. */
  tableNumber?: string;
  /** Names of others at the same table (excludes self). */
  tablemates?: string[];
}

export interface InviteInvalidResponse {
  valid: false;
}

export type InviteResponse = InviteValidResponse | InviteInvalidResponse;

export interface SubmitRsvpRequest {
  status: RsvpStatus;
  adults: number;
  kids: number;
}

export interface AdminGuestRow {
  token: string;
  name: string;
  email?: string;
  phone?: string;
  isTest?: boolean;
  invitation?: Guest["invitation"];
  viewedAt?: string;
  caps: RsvpCaps;
  revoked?: boolean;
  rsvp?: Rsvp;
  tableNumber?: string;
}

export interface CreateGuestRequest {
  eventId: string;
  name: string;
  email: string;
  phone?: string;
  isTest?: boolean;
  caps?: RsvpCaps;
}

export interface UpdateGuestRequest {
  name?: string;
  email?: string;
  phone?: string;
  isTest?: boolean;
  caps?: RsvpCaps;
  revoked?: boolean;
  tableNumber?: string;
  /** When true, wipes invitation, viewedAt, and rsvp — returns guest to Pending. */
  reset?: boolean;
  /** Admin override of the guest's RSVP. Pass null to clear it. */
  rsvp?: { status: RsvpStatus; adults: number; kids: number } | null;
}

export interface CreateGuestResponse {
  token: string;
}

export interface AdminListResponse {
  event: {
    eventId: string;
    title: string;
    date: string;
    location?: string;
    address?: string;
    rsvpCaps: RsvpCaps;
    rsvpClosed?: boolean;
    invitationSender?: EventConfig["invitationSender"];
  };
  guests: AdminGuestRow[];
  summary: {
    total: number;
    invitesSent: number;
    viewedNotResponded: number;
    respondedYes: number;
    respondedNo: number;
    attendingAdults: number;
    attendingKids: number;
    testCount: number;
    testAttendingAdults: number;
    testAttendingKids: number;
  };
}

export interface UpdateEventSettingsRequest {
  senderEmail?: string;
  senderName?: string;
  title?: string;
  date?: string;
  location?: string;
  address?: string;
  rsvpCaps?: RsvpCaps;
  rsvpClosed?: boolean;
}

export interface AdminEventSummary {
  eventId: string;
  title: string;
  date: string;
  location?: string;
}

export interface CreateEventRequest {
  eventId: string;
  title: string;
  date: string;
  location?: string;
  address?: string;
  rsvpCaps: RsvpCaps;
}

export interface SeatsResponse {
  eventTitle: string;
  guests: Array<{ name: string; tableNumber: string }>;
}

export const DEFAULT_CAPS: RsvpCaps = { adults: 2, kids: 3 };

/**
 * Validate an RSVP submission against the event caps. Used on both client
 * (for UX) and server (as the authority). Returns a normalized RSVP payload
 * on success so callers never persist unclamped values.
 */
export function validateRsvp(
  input: Partial<SubmitRsvpRequest>,
  caps: RsvpCaps
): { ok: true; value: SubmitRsvpRequest } | { ok: false; error: string } {
  const status = input.status;
  if (status !== "yes" && status !== "no") {
    return { ok: false, error: "status must be 'yes' or 'no'" };
  }

  if (status === "no") {
    return { ok: true, value: { status: "no", adults: 0, kids: 0 } };
  }

  const adults = Number(input.adults);
  const kids = Number(input.kids);
  const aMax = capLimit(caps.adults);
  const kMax = capLimit(caps.kids);

  if (!Number.isInteger(adults) || adults < 0 || adults > aMax) {
    return {
      ok: false,
      error:
        caps.adults === "unlimited"
          ? "adults must be 0 or more"
          : `adults must be between 0 and ${caps.adults}`,
    };
  }
  if (!Number.isInteger(kids) || kids < 0 || kids > kMax) {
    return {
      ok: false,
      error:
        caps.kids === "unlimited"
          ? "kids must be 0 or more"
          : `kids must be between 0 and ${caps.kids}`,
    };
  }
  if (adults + kids < 1) {
    return { ok: false, error: "an attending party must include at least 1 person" };
  }

  return { ok: true, value: { status: "yes", adults, kids } };
}
